"use client";

import { useState } from "react";
import Image from "next/image";
import { Expand } from "lucide-react";
import { useTranslations } from "next-intl";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PDF_SRC = "/SAIYAN-Booty-Builder-12-tjedni-program.pdf";

export default function BootyBuilderSection() {
  const [open, setOpen] = useState(false);
  const t = useTranslations("BootyBuilder");
  const bullets = t.raw("bullets") as string[];

  // Touch devices (notably iOS Safari) render a PDF in an iframe as a single,
  // non-scrollable page, so hand them off to the browser's native PDF viewer.
  const openProgram = () => {
    if (window.matchMedia("(pointer: coarse)").matches) {
      window.open(PDF_SRC, "_blank", "noopener");
    } else {
      setOpen(true);
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-6 py-12 sm:px-8 lg:px-10">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-8">

        {/* Left — image, opens PDF in a popup */}
        <div className="w-full flex-shrink-0 lg:w-[49%]">
          <button
            type="button"
            onClick={openProgram}
            aria-label={t("openAria")}
            className="group relative block aspect-[6/5] w-full overflow-hidden rounded-2xl lg:aspect-auto lg:h-full"
          >
            <Image
              src="/booty_builder.png"
              alt={t("imageAlt")}
              fill
              className="object-cover"
            />
            <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
              <Expand className="h-4 w-4" />
            </span>
          </button>
        </div>

        {/* Right — text */}
        <div className="flex flex-col gap-6 lg:w-[51%]">
          <h2 className="text-4xl font-bold leading-none text-[#18181b] sm:text-5xl">
            {t("heading")}
          </h2>

          <p className="text-base leading-normal text-[#18181b]">
            {t("paragraph")}
          </p>

          <ul className="flex flex-col gap-3">
            {bullets.map((item) => (
              <li key={item} className="flex items-start gap-2 text-base leading-relaxed text-[#3f3f46]">
                <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#18181b]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={openProgram}
              className="rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-brand-foreground shadow-sm transition-opacity hover:opacity-90"
            >
              {t("ctaButton")}
            </button>

            <a
              href={PDF_SRC}
              download
              className="rounded-full border border-[#E4E4E7] bg-white px-6 py-2.5 text-sm font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5]"
            >
              {t("downloadButton")}
            </a>
          </div>
        </div>
      </div>

      {/* PDF popup */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton
          className="h-[85vh] max-w-4xl border-none bg-transparent p-0 shadow-none sm:max-w-4xl [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:bg-black/50 [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:text-white [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:hover:bg-black/70"
        >
          <DialogTitle className="sr-only">{t("dialogTitle")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("dialogDescription")}
          </DialogDescription>
          <div className="h-full w-full overflow-hidden rounded-2xl bg-white">
            <iframe
              src={PDF_SRC}
              title={t("dialogTitle")}
              className="h-full w-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
